import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { runes } from 'runes2';

import PlainModal from '@whelk/components/src/Base/PlainModal';
import { Toast } from '@whelk/components';
import Button from '@whelk/components/src/Base/Button';
import { InputClearIcon } from '@whelk/assets/src/web/new-icons';
import { RootState } from '@whelk/store';
import { useHistoryItem, useRenameHistory } from '@whelk/hooks/src/useHistoryList';
import { CHAT_TITLE_MAX_LENGTH } from '@whelk/const/src/history';

import './style.scss';

const RenameHistoryModal = () => {
  const { t } = useTranslation();
  const { confirmRenameChat, cancelRenameChat } = useRenameHistory();
  const open = useSelector((state: RootState) => state.history.isHistoryRenameModalOpen);
  const chatId = useSelector((state: RootState) => state.history.renamingChatId);
  const { historyItem: item } = useHistoryItem(chatId ?? '');
  const trackParams = useSelector((state: RootState) => state.history.renamingHistoryTrackParams);
  const originTitle = (item?.chatTitle ?? '').slice(0, CHAT_TITLE_MAX_LENGTH);
  const [currentTitle, setCurrentTitle] = useState(originTitle);
  const [isComposing, setIsComposing] = useState(false);

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  useEffect(() => {
    setCurrentTitle(originTitle);
  }, [originTitle]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
  };

  const handleKeyDown = (e: any) => {
    if (isComposing) {
      return;
    }
    if (e.key === 'Enter') {
      onConfirm();
    }
  };

  const onConfirm = () => {
    if (!chatId) {
      return;
    }

    if (!currentTitle) {
      Toast.warning(t('history_rename_empty_toast'));
      return;
    }

    if (currentTitle === originTitle) {
      cancelRenameChat(chatId, trackParams);
    } else {
      confirmRenameChat(chatId, currentTitle, trackParams);
    }
  };

  const onCancel = () => {
    cancelRenameChat(chatId, trackParams);
  };

  return (
    <PlainModal
      open={open && item !== undefined}
      onClickMask={onCancel}
      title={t('rename_conversation')}
      onClose={onCancel}
      closeOnEscape={true}
      size='md'
    >
      <Input
        rootClassName='history-rename-input'
        count={{
          show: true,
          max: CHAT_TITLE_MAX_LENGTH,
          strategy: (txt) => runes(txt).length,
          exceedFormatter: (txt, { max }) => runes(txt).slice(0, max).join(''),
        }}
        allowClear={{ clearIcon: <InputClearIcon /> }}
        autoFocus={true}
        defaultValue={originTitle}
        onKeyDown={handleKeyDown}
        onChange={onChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        placeholder={t('history_rename_placeholder', { count: CHAT_TITLE_MAX_LENGTH })}
      />
      <div className='mt-8 flex w-full justify-end gap-4 text-[14px] leading-[20px]'>
        <Button variant='gray' onClick={onCancel}>
          {t('general_cancel')}
        </Button>
        <Button
          disabled={!currentTitle || currentTitle === originTitle}
          variant='blue'
          onClick={onConfirm}
        >
          {t('general_confirm')}
        </Button>
      </div>
    </PlainModal>
  );
};

export default RenameHistoryModal;
